import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';
import LandingHeader from '../../components/LandingHeader/LandingHeader';
import Footer from "../../components/Footer/Footer";

const SECTIONS = [
    { id: 'commitment',    label: 'Our Commitment' },
    { id: 'collection',   label: 'Information We Collect' },
    { id: 'sources',      label: 'Sources' },
    { id: 'use',          label: 'How We Use Information' },
    { id: 'ai',           label: 'How Welby Uses AI' },
    { id: 'sharing',      label: 'How We Share Information' },
    { id: 'advertising',  label: 'Sale & Advertising' },
    { id: 'consent',      label: 'Consent' },
    { id: 'rights',       label: 'Your Privacy Rights' },
    { id: 'retention',    label: 'Deletion & Retention' },
    { id: 'security',     label: 'Security' },
    { id: 'incidents',    label: 'Security Incidents' },
    { id: 'hipaa',        label: 'HIPAA' },
    { id: 'children',     label: "Children's Privacy" },
    { id: 'communications', label: 'Communications' },
    { id: 'cookies',      label: 'Cookies' },
    { id: 'thirdparty',   label: 'Third-Party Services' },
    { id: 'changes',      label: 'Policy Changes' },
    { id: 'contact',      label: 'Contact Us' },
];

const PrivacyPolicy = () => {
    const [activeSection, setActiveSection] = useState('commitment');
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) setActiveSection(entry.target.id);
                });
            },
            { rootMargin: '-20% 0px -70% 0px' }
        );
        SECTIONS.forEach(({ id }) => {
            const el = document.getElementById(id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);

    const scrollTo = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        setMenuOpen(false);
    };

    return (
        <div className="pp-root">
            <LandingHeader />

            <div className="pp-layout">
                {/* ── Sidebar nav ── */}
                <aside className="pp-sidebar">
                    <p className="pp-sidebar-label">On this page</p>
                    <nav>
                        <ul className="pp-nav-list">
                            {SECTIONS.map(({ id, label }, i) => (
                                <li key={id}>
                                    <button
                                        className={`pp-nav-item ${activeSection === id ? 'pp-nav-item--active' : ''}`}
                                        onClick={() => scrollTo(id)}
                                    >
                                        <span className="pp-nav-num">{String(i + 1).padStart(2, '0')}</span>
                                        <span className="pp-nav-label">{label}</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </nav>
                </aside>

                {/* ── Mobile nav toggle ── */}
                <button className="pp-mobile-nav-toggle" onClick={() => setMenuOpen((o) => !o)}>
                    {menuOpen ? 'Close' : 'Jump to section ↓'}
                </button>
                {menuOpen && (
                    <div className="pp-mobile-nav">
                        {SECTIONS.map(({ id, label }, i) => (
                            <button key={id} className="pp-mobile-nav-item" onClick={() => scrollTo(id)}>
                                <span className="pp-nav-num">{String(i + 1).padStart(2, '0')}</span> {label}
                            </button>
                        ))}
                    </div>
                )}

                {/* ── Main content ── */}
                <main className="pp-content">

                    {/* Hero */}
                    <div className="pp-hero">
                        <p className="pp-hero-eyebrow">Legal</p>
                        <h1 className="pp-hero-title">Privacy Policy</h1>
                        <p className="pp-hero-sub">
                            UME Health is committed to protecting the personal and sensitive information you
                            share with us. This policy explains what we collect, how we use it, and the rights
                            you have over your data.
                        </p>
                        <div className="pp-hero-dates">
                            <span><strong>Effective:</strong> January 1, 2026</span>
                            <span className="pp-dot" />
                            <span><strong>Last updated:</strong> August 1, 2026</span>
                        </div>
                    </div>

                    <div className="pp-divider" />

                    {/* 1 */}
                    <section id="commitment" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">01</span>
                            <h2>Our Commitment to Your Privacy</h2>
                        </div>
                        <p>
                            UME Health, LLC. ("UME Health," "UME," "we," "us," or "our") respects the personal
                            and sensitive nature of the information people use to manage their health.
                        </p>
                        <p>This Privacy Policy explains how we collect, use, process, disclose, retain, and protect information through:</p>
                        <ul>
                            <li>the UME Health website at umehealthapp.com;</li>
                            <li>the Welby mobile or web application;</li>
                            <li>Welby's voice, messaging, document-upload, camera, reminder, and caregiver-support features;</li>
                            <li>pilot programs, surveys, demonstrations, and customer-support services; and</li>
                            <li>other services that link to this Privacy Policy.</li>
                        </ul>
                        <p>Together, these are referred to as the "Services."</p>
                        <p>
                            Welby is an AI-powered health companion designed to help individuals and caregivers
                            better understand, organize, and follow through on healthcare information and
                            instructions. Depending on the features available, Welby may help users upload
                            healthcare documents, identify action items, create reminders, organize appointments,
                            and manage health-related tasks.
                        </p>
                        <p>
                            By accessing the Services, you acknowledge this Privacy Policy. When applicable law
                            requires your consent to collect or share consumer health data, we will request that
                            consent separately and before the relevant collection or sharing occurs.
                        </p>
                    </section>

                    {/* 2 */}
                    <section id="collection" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">02</span>
                            <h2>Information We Collect</h2>
                        </div>
                        <p>The information we collect depends on how you interact with UME Health and which Welby features you choose to use.</p>

                        <h3>A. Account and Contact Information</h3>
                        <p>We may collect:</p>
                        <ul>
                            <li>first and last name;</li>
                            <li>email address;</li>
                            <li>phone number;</li>
                            <li>mailing address;</li>
                            <li>date of birth or age range;</li>
                            <li>username and password;</li>
                            <li>preferred language;</li>
                            <li>communication preferences; and</li>
                            <li>caregiver, family member, emergency contact, or authorized representative information.</li>
                        </ul>

                        <h3>B. Consumer Health Data</h3>
                        <p>When you choose to enter, upload, photograph, forward, dictate, or otherwise provide health-related information, we may collect:</p>
                        <ul>
                            <li>discharge instructions;</li>
                            <li>after-visit summaries;</li>
                            <li>treatment or care instructions;</li>
                            <li>medication names, schedules, and related information;</li>
                            <li>appointment, referral, testing, and follow-up information;</li>
                            <li>healthcare-provider and facility information;</li>
                            <li>health-plan or insurance information;</li>
                            <li>reported symptoms, conditions, diagnoses, procedures, or care needs;</li>
                            <li>health-related tasks, reminders, and completion status;</li>
                            <li>information about barriers to completing care;</li>
                            <li>healthcare documents, images, photographs, and attachments;</li>
                            <li>questions or conversations with Welby;</li>
                            <li>information entered by an authorized caregiver or representative; and</li>
                            <li>other health information you voluntarily provide.</li>
                        </ul>
                        <div className="pp-callout">
                            Some of this information may qualify as "consumer health data," "sensitive personal
                            information," or a similar category under applicable privacy laws.
                        </div>

                        <h3>C. Camera, Image, Audio, and Voice Information</h3>
                        <p>When you give permission to use these features, we may collect or process:</p>
                        <ul>
                            <li>photographs of documents;</li>
                            <li>photographs submitted for available image-analysis features;</li>
                            <li>files stored on your device;</li>
                            <li>voice recordings, voice commands, or audio input;</li>
                            <li>transcripts created from audio or voice interactions; and</li>
                            <li>metadata associated with uploaded files or images.</li>
                        </ul>
                        <p>
                            UME Health does not use voice, face, or image information to uniquely identify you
                            through biometric recognition unless we clearly disclose that use and obtain any
                            consent required by law.
                        </p>
                        <p>
                            You can generally manage camera, microphone, photo-library, and file-access
                            permissions through your device settings. Disabling a permission may prevent the
                            related feature from working.
                        </p>

                        <h3>D. Communications and Support Information</h3>
                        <p>We may collect information that you provide when you contact customer support, send an email or message, respond to a survey, participate in a pilot program or usability test, attend a demonstration, submit feedback, or communicate with UME Health representatives.</p>

                        <h3>E. Website, Device, and Usage Information</h3>
                        <p>We may automatically collect:</p>
                        <ul>
                            <li>IP address;</li>
                            <li>browser and device type;</li>
                            <li>operating system;</li>
                            <li>device identifiers;</li>
                            <li>referring webpage;</li>
                            <li>general geographic region derived from an IP address;</li>
                            <li>access dates and times;</li>
                            <li>pages or features viewed;</li>
                            <li>clicks, taps, and navigation activity;</li>
                            <li>app performance information;</li>
                            <li>crash reports and diagnostic data; and</li>
                            <li>security and login activity.</li>
                        </ul>
                        <p>We do not collect precise location information unless a specific feature requires it, we clearly explain the purpose, and you provide any consent required by law.</p>

                        <h3>F. Payment Information</h3>
                        <p>
                            If UME Health later offers paid Services, payment information may be processed by a
                            third-party payment processor. UME Health may receive limited transaction information
                            such as payment status, purchase amount, or billing contact information, but may not
                            receive or store the complete payment-card number.
                        </p>
                    </section>

                    {/* 3 */}
                    <section id="sources" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">03</span>
                            <h2>Sources of Information</h2>
                        </div>
                        <p>We may collect information:</p>
                        <ul>
                            <li>directly from you;</li>
                            <li>from a caregiver, family member, or representative whom you authorize;</li>
                            <li>from documents, images, audio, or files you submit;</li>
                            <li>automatically from your device or interaction with the Services;</li>
                            <li>from healthcare providers, health plans, community organizations, or other partners when you authorize the disclosure or when the disclosure is otherwise legally permitted;</li>
                            <li>from vendors that help us provide the Services; and</li>
                            <li>from publicly available sources when appropriate.</li>
                        </ul>
                    </section>

                    {/* 4 */}
                    <section id="use" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">04</span>
                            <h2>How We Use Information</h2>
                        </div>
                        <p>We may use personal information and consumer health data to:</p>
                        <ul>
                            <li>create and maintain your account;</li>
                            <li>provide the Welby features you request;</li>
                            <li>extract and organize information from healthcare documents;</li>
                            <li>identify potential appointments, medications, referrals, tests, instructions, and other action items;</li>
                            <li>turn information into plain-language tasks, reminders, or summaries;</li>
                            <li>provide voice, conversational, accessibility, and language-support features;</li>
                            <li>support caregiver participation when authorized;</li>
                            <li>deliver alerts, reminders, and account communications;</li>
                            <li>respond to questions and customer-support requests;</li>
                            <li>personalize the Services based on your selected preferences;</li>
                            <li>operate pilot programs and evaluate usability;</li>
                            <li>improve accessibility, reliability, safety, and performance;</li>
                            <li>troubleshoot errors and technical problems;</li>
                            <li>protect accounts and prevent unauthorized activity, misuse, or fraud;</li>
                            <li>maintain business, legal, and security records;</li>
                            <li>comply with applicable laws and lawful governmental requests;</li>
                            <li>enforce our agreements and protect our legal rights; and</li>
                            <li>fulfill another purpose that we disclose when the information is collected.</li>
                        </ul>
                        <p>Where reasonably possible, we use aggregated or de-identified information for analytics, research, product improvement, and business planning.</p>
                    </section>

                    {/* 5 */}
                    <section id="ai" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">05</span>
                            <h2>How Welby Uses Artificial Intelligence</h2>
                        </div>
                        <p>Welby may use artificial intelligence, optical character recognition, natural-language processing, voice technology, and other automated tools to process information you provide.</p>
                        <p>These technologies may be used to:</p>
                        <ul>
                            <li>read uploaded documents or images;</li>
                            <li>identify important information;</li>
                            <li>organize instructions;</li>
                            <li>generate suggested tasks or reminders;</li>
                            <li>produce plain-language explanations;</li>
                            <li>transcribe voice interactions;</li>
                            <li>respond conversationally; and</li>
                            <li>support the operation and improvement of the Services.</li>
                        </ul>
                        <div className="pp-callout pp-callout--warning">
                            Automated results may be incomplete or inaccurate. Welby is intended to support
                            organization and follow-through and is <strong>not a substitute</strong> for a licensed
                            healthcare professional, diagnosis, treatment, or emergency care.
                        </div>
                        <p>
                            UME Health does not authorize its service providers to use identifiable consumer
                            health data to train unrelated public or general-purpose AI models unless we disclose
                            that use and obtain any legally required consent.
                        </p>
                    </section>

                    {/* 6 */}
                    <section id="sharing" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">06</span>
                            <h2>How We Share Information</h2>
                        </div>
                        <p>We may disclose information only as described in this Policy, as needed to provide a requested service, as permitted or required by law, or with your direction or consent.</p>

                        <h3>A. Service Providers and Processors</h3>
                        <p>We may disclose information to vendors that perform services for us, such as cloud hosting, software development, document processing, AI and voice-processing services, email and notification delivery, customer support, cybersecurity, analytics, payment processing, and professional legal or compliance services. These providers are authorized to process information only for contracted purposes.</p>

                        <h3>B. Authorized Caregivers and Representatives</h3>
                        <p>We may disclose information to a caregiver, family member, or representative when you authorize that person, when the person is permitted to access the applicable account or feature, or when another lawful basis permits the disclosure. You are responsible for carefully choosing anyone whom you authorize to access your information.</p>

                        <h3>C. Healthcare and Community Partners</h3>
                        <p>When a Welby service is offered through or in cooperation with a healthcare provider, health plan, employer-sponsored benefit, senior center, community organization, or other partner, we may disclose information as necessary to provide the requested service. Where required, we will obtain separate consent before sharing consumer health data with such a partner.</p>

                        <h3>D. Legal, Safety, and Security Disclosures</h3>
                        <p>We may disclose information when we reasonably believe it is necessary to comply with applicable law, investigate fraud or a security incident, protect the rights or safety of UME Health or our users, enforce our terms, or establish legal claims.</p>

                        <h3>E. Business Transactions</h3>
                        <p>Information may be transferred as part of a merger, acquisition, restructuring, bankruptcy, sale of assets, or similar transaction. Any successor that receives consumer health data will be expected to assume applicable privacy obligations.</p>
                    </section>

                    {/* 7 */}
                    <section id="advertising" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">07</span>
                            <h2>Sale, Advertising, and Third-Party Tracking</h2>
                        </div>
                        <div className="pp-highlight-row">
                            <div className="pp-highlight-card">
                                <p><strong>We do not sell consumer health data.</strong></p>
                            </div>
                            <div className="pp-highlight-card">
                                <p><strong>We do not use identifiable consumer health data for targeted advertising.</strong></p>
                            </div>
                        </div>
                        <p>
                            We may use carefully configured analytics or performance tools to understand website
                            and application usage. UME Health does not intentionally provide these tools with
                            the contents of healthcare documents or other consumer health data for cross-site
                            advertising.
                        </p>
                        <p>
                            Before publication, UME Health will review its website, app, analytics tools,
                            cookies, pixels, software-development kits, and vendor settings to ensure that
                            actual practices match this section.
                        </p>
                    </section>

                    {/* 8 */}
                    <section id="consent" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">08</span>
                            <h2>Consent for Consumer Health Data</h2>
                        </div>
                        <p>We collect consumer health data with your affirmative, voluntary consent, or to the extent reasonably necessary to provide a product or service that you request. When separate consent is legally required to share consumer health data, we will request that consent separately from consent to collect it.</p>
                        <p>A request for consent may explain the categories of health data involved, why the data is being collected or shared, how it will be used, the categories of recipients, and how to withdraw consent.</p>
                        <p>You may withdraw consent or request that we stop collecting or sharing consumer health data by using the privacy-request process described below. Withdrawal will not affect processing that lawfully occurred before the withdrawal and may limit our ability to provide certain features.</p>
                    </section>

                    {/* 9 */}
                    <section id="rights" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">09</span>
                            <h2>Your Privacy Rights</h2>
                        </div>
                        <p>Depending on where you live and applicable law, you may have the right to:</p>
                        <ul>
                            <li>confirm whether we collect, use, share, or sell your information;</li>
                            <li>access information we maintain about you;</li>
                            <li>request correction of inaccurate information;</li>
                            <li>request a list of third parties with whom we shared consumer health data;</li>
                            <li>withdraw consent;</li>
                            <li>request that we stop collecting or sharing consumer health data;</li>
                            <li>request deletion of personal information or consumer health data;</li>
                            <li>obtain a copy of certain information;</li>
                            <li>opt out of legally defined sales or targeted advertising;</li>
                            <li>designate an authorized agent to submit a request; and</li>
                            <li>appeal a decision denying a privacy request.</li>
                        </ul>
                        <p>We will not unlawfully discriminate against you for exercising a privacy right.</p>

                        <h3>Submitting a Privacy Request</h3>
                        <div className="pp-contact-box">
                            <p>You may submit a request by email at:</p>
                            <a href="mailto:hello@umehealthapp.com" className="pp-contact-email">hello@umehealthapp.com</a>
                            <p className="pp-contact-note">We may request information to verify your identity. We will respond within the timeframe required by applicable law. For qualifying Nevada consumer-health-data requests, UME Health will generally respond within 45 days after authentication.</p>
                        </div>

                        <h3>Appeals</h3>
                        <p>You may appeal our refusal to act on a request by emailing <a href="mailto:hello@umehealthapp.com">hello@umehealthapp.com</a> with the subject line <strong>Privacy Request Appeal</strong>. We will review and respond within the period required by applicable law.</p>
                    </section>

                    {/* 10 */}
                    <section id="retention" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">10</span>
                            <h2>Deletion and Retention</h2>
                        </div>
                        <p>We retain information only for as long as reasonably necessary to provide the Services, maintain an account, fulfill the purpose for which the information was collected, meet contractual or legal obligations, resolve disputes, protect security and prevent fraud, and enforce our agreements.</p>
                        <p>When a valid deletion request applies, we will delete the covered information and direct applicable processors or other recipients to do the same, subject to permitted exceptions. Information stored in secure backup or archival systems may remain for a limited period permitted by law. We may retain aggregated or properly de-identified information that can no longer reasonably be linked to you.</p>
                    </section>

                    {/* 11 */}
                    <section id="security" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">11</span>
                            <h2>Security</h2>
                        </div>
                        <p>UME Health uses reasonable administrative, technical, and physical safeguards designed to protect personal information and consumer health data from unauthorized access, acquisition, alteration, loss, misuse, or disclosure. Safeguards may include:</p>
                        <ul>
                            <li>access controls;</li>
                            <li>authentication procedures;</li>
                            <li>encryption where appropriate;</li>
                            <li>vendor-management requirements;</li>
                            <li>staff and contractor confidentiality obligations;</li>
                            <li>security monitoring;</li>
                            <li>backup and recovery procedures; and</li>
                            <li>incident-response processes.</li>
                        </ul>
                        <div className="pp-callout pp-callout--warning">
                            No website, application, transmission method, or storage system is completely secure.
                            We therefore cannot guarantee absolute security. Do not send healthcare documents or
                            highly sensitive information to an ordinary email address unless UME Health has
                            specifically instructed you that the method is approved for that purpose.
                        </div>
                    </section>

                    {/* 12 */}
                    <section id="incidents" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">12</span>
                            <h2>Security Incidents and Breach Notification</h2>
                        </div>
                        <p>If UME Health discovers a security incident involving personal information, we will investigate and take reasonable steps to contain, assess, and address the incident. We will notify affected individuals, governmental authorities, business partners, or others when notification is required by applicable law or contract.</p>
                    </section>

                    {/* 13 */}
                    <section id="hipaa" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">13</span>
                            <h2>HIPAA and Health Information</h2>
                        </div>
                        <p>The Health Insurance Portability and Accountability Act (HIPAA) does not apply to every healthcare application or every type of health information. UME Health may become subject to HIPAA for particular information or services when it acts as a business associate of a healthcare provider, health plan, or another HIPAA-covered entity.</p>
                        <p>When UME Health provides Services directly to consumers and is not acting for a HIPAA-covered entity, HIPAA may not apply. That information remains subject to this Privacy Policy and other applicable federal and state privacy laws.</p>
                        <div className="pp-callout">
                            This Privacy Policy is not a HIPAA Notice of Privacy Practices.
                        </div>
                    </section>

                    {/* 14 */}
                    <section id="children" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">14</span>
                            <h2>Children's Privacy</h2>
                        </div>
                        <p>The Services are not directed to children under 13, and children under 13 may not create an independent account. A parent, legal guardian, or properly authorized caregiver may provide information concerning a child only when the applicable feature permits it, the person has legal authority to do so, and any required consent has been provided. If we learn that a child under 13 has provided personal information without appropriate authorization, we will take reasonable steps to delete it.</p>
                    </section>

                    {/* 15 */}
                    <section id="communications" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">15</span>
                            <h2>Communications</h2>
                        </div>
                        <p>We may send administrative or service-related communications including account notices, reminders requested through the Services, security alerts, support responses, changes to Services or policies, and other messages necessary to operate your account.</p>
                        <p>You may opt out of promotional emails by selecting the unsubscribe link or contacting us. Opting out of promotional communications will not stop essential account or service communications. Text-message and notification preferences may be managed through your account, device settings, or the instructions provided in the applicable message.</p>
                    </section>

                    {/* 16 */}
                    <section id="cookies" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">16</span>
                            <h2>Cookies and Similar Technologies</h2>
                        </div>
                        <p>We may use cookies, local storage, software-development kits, and similar technologies to maintain sessions, remember preferences, support authentication, understand website or application performance, identify errors, improve usability, and protect the Services. You may be able to block some cookies through your browser or device settings. Certain features may not operate correctly when cookies or local storage are disabled.</p>
                    </section>

                    {/* 17 */}
                    <section id="thirdparty" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">17</span>
                            <h2>Third-Party Services and Links</h2>
                        </div>
                        <p>The Services may contain links to, or integrations with, third-party websites and services. Those organizations operate under their own terms and privacy policies. UME Health is not responsible for a third party's independent privacy practices. We encourage you to review the privacy information provided by any third party before submitting information.</p>
                    </section>

                    {/* 18 */}
                    <section id="changes" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">18</span>
                            <h2>Changes to This Privacy Policy</h2>
                        </div>
                        <p>We may update this Privacy Policy as our Services, technologies, vendors, or legal obligations change. When changes are material, we will provide notice through one or more appropriate methods such as posting a prominent notice on our website, displaying a notice within the application, sending an email or account communication, or requesting new consent when required.</p>
                        <p>The "Last Updated" date will identify the most recent revision. Material changes affecting consumer health data will apply only as permitted by law, and we will obtain affirmative consent where legally required.</p>
                    </section>

                    {/* 19 */}
                    <section id="contact" className="pp-section">
                        <div className="pp-section-header">
                            <span className="pp-section-num">19</span>
                            <h2>Contact UME Health</h2>
                        </div>
                        <p>Questions, concerns, or privacy requests may be submitted to:</p>
                        <div className="pp-contact-card">
                            <p className="pp-contact-name">UME Health, LLC.</p>
                            <p>35 E. Horizon Ridge Suite 110<br />Henderson, NV 89002</p>
                            <div className="pp-contact-links">
                                <a href="https://umehealthapp.com" target="_blank" rel="noreferrer">umehealthapp.com</a>
                                <a href="mailto:hello@umehealthapp.com">hello@umehealthapp.com</a>
                                <a href="tel:+17027584849">(702) 758-4849</a>
                            </div>
                        </div>
                    </section>

                    <div className="pp-footer-note">
                        <p>© {new Date().getFullYear()} UME Health, LLC. All rights reserved.</p>
                        <Link to="/" className="pp-back-link">← Back to home</Link>
                    </div>

                </main>
            </div>
            <Footer />
        </div>
    );
};

export default PrivacyPolicy;